import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { Papa } from 'ngx-papaparse';
import { FilterSummaryComponent } from 'src/app/shared/components/filter-summary/filter-summary.component';
import { PaginationComponent } from 'src/app/shared/components/pagination/pagination.component';
import { ToggleComponent } from 'src/app/shared/components/toggle/toggle.component';
import { SECONDS } from 'src/app/shared/functions/time';
import { objectContainsTerm } from 'src/app/shared/utils/object-search.util';
import { canUseSampleData } from 'src/app/shared/utils/sample-data-toggle.util';
import * as fromStore from 'src/app/store';

import { P95UtilizationListComponent } from './components/p95-utilization-list/p95-utilization-list.component';
import { P95UtilizationService } from './services/p95-utilization/p95-utilization.service';
import { IP95UtilizationService } from './services/p95-utilization/p95-utilization.service.types';

interface DurationOption {
  label: '1 Week' | '1 Month' | '1 Quarter';
  value: '1w' | '1m' | '3m';
}

interface P95CsvDataRow {
  Location: string;
  Link: string;
  'In %': string;
  'Out %': string;
  'Speed In (Mbps)': string;
  'Speed Out (Mbps)': string;
}

const DURATION_OPTIONS: ReadonlyArray<DurationOption> = Object.freeze([
  { label: '1 Week', value: '1w' },
  { label: '1 Month', value: '1m' },
  { label: '1 Quarter', value: '3m' },
]);

@Component({
  standalone: true,
  selector: 'app-p95-utilization-page',
  templateUrl: './p95-utilization-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    RouterModule,
    P95UtilizationListComponent,
    MatIconModule,
    MatTooltipModule,
    FormsModule,
    ToggleComponent,
    FilterSummaryComponent,
    PaginationComponent,
  ],
})
export class P95UtilizationPageComponent implements OnInit, OnDestroy {
  public readonly observations$ = inject<IP95UtilizationService>(
    P95UtilizationService,
  );

  private readonly router$ = inject(Router);

  private readonly activatedRoute$ = inject(ActivatedRoute);

  private readonly papa$ = inject(Papa);

  private readonly store = inject(Store);

  private readonly destroyRef = inject(DestroyRef);

  public readonly durationOptions = DURATION_OPTIONS;

  public readonly selectedDuration$ = signal<'1w' | '1m' | '3m'>('1m');

  public readonly isDurationDropdownOpen = signal(false);

  public readonly searchTerm$ = signal<string>('');

  public readonly selectedLocation$ = signal<string>('');

  public readonly isLocationDropdownOpen = signal(false);

  public readonly pageIndex$ = signal<number>(0);

  public readonly pageSize$ = signal<number>(10);

  public readonly isPageSizeDropdownOpen = signal(false);

  public readonly pageSizeOptions = [10, 25, 50, 100];

  readonly showMockData = signal(false);

  readonly canShowSampleDataToggle = signal(false);

  readonly isExporting = signal(false);

  public readonly csvFileName = 'P95-Utilization.csv';

  public readonly filteredItemsCount$ = computed(() => {
    const records = this.observations$.records$() ?? [];
    const searchTerm = this.searchTerm$().trim();
    const selectedLocation = this.selectedLocation$().trim();

    let filtered = records;

    if (searchTerm) {
      filtered = filtered.filter((record) =>
        objectContainsTerm(record, searchTerm),
      );
    }

    if (selectedLocation) {
      filtered = filtered.filter(
        (record) => record.name_customer_location === selectedLocation,
      );
    }

    return filtered.length;
  });

  public readonly totalPages$ = computed(() => {
    const itemsCount = this.filteredItemsCount$();
    const pageSize = this.pageSize$();
    return Math.ceil(itemsCount / pageSize);
  });

  public readonly uniqueLocations$ = computed(() => {
    const records = this.observations$.records$() ?? [];
    const locations = new Set<string>();
    records.forEach((record) => {
      if (record.name_customer_location) {
        locations.add(record.name_customer_location);
      }
    });
    return Array.from(locations).sort();
  });

  public readonly filteredRecordsForCsv$ = computed(() => {
    const records = this.observations$.records$() ?? [];
    const searchTerm = this.searchTerm$().trim();
    const selectedLocation = this.selectedLocation$().trim();

    let filtered = records;

    if (searchTerm) {
      filtered = filtered.filter((record) =>
        objectContainsTerm(record, searchTerm),
      );
    }

    if (selectedLocation) {
      filtered = filtered.filter(
        (record) => record.name_customer_location === selectedLocation,
      );
    }

    return filtered;
  });

  public readonly filterParts$ = computed(() => {
    const parts: string[] = [];
    const searchTerm = this.searchTerm$().trim();
    const selectedLocation = this.selectedLocation$().trim();
    const selectedDuration = this.selectedDuration$();

    if (searchTerm) {
      parts.push(`Search: ${searchTerm}`);
    }
    if (selectedLocation) {
      parts.push(`Location: ${selectedLocation}`);
    }
    if (selectedDuration !== '1m') {
      const durationLabel = this.durationOptions.find(
        (opt) => opt.value === selectedDuration,
      )?.label;
      if (durationLabel) {
        parts.push(`Duration: ${durationLabel}`);
      }
    }

    return parts;
  });

  public readonly csvData$ = computed<P95CsvDataRow[]>(() =>
    this.filteredRecordsForCsv$().map((record) => ({
      Location: record.name_customer_location ?? '',
      Link: record.dim_connection_id ?? '',
      'In %': this.formatPercentage(record.p95_utilization_rx_pct),
      'Out %': this.formatPercentage(record.p95_utilization_tx_pct),
      'Speed In (Mbps)': this.formatSpeed(record.provisioned_speed_rx_mbps),
      'Speed Out (Mbps)': this.formatSpeed(record.provisioned_speed_tx_mbps),
    })),
  );

  public readonly filtersChangeEffect = effect(() => {
    // Duration changes trigger data reload from API.
    this.selectedDuration$();
    this.pageIndex$.set(0);
    this.getPage({
      // Restarts page when new filters are applied
      debounce: SECONDS(1),
    });
  });

  public readonly searchPageResetEffect = effect(() => {
    this.searchTerm$();
    this.pageIndex$.set(0);
  });

  ngOnInit(): void {
    this.applyQueryParams(this.activatedRoute$.snapshot.queryParams);
    this.queryParamsSubscription = this.activatedRoute$.queryParams.subscribe(
      (params) => this.applyQueryParams(params),
    );

    this.store
      .select(fromStore.getCurrentUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.canShowSampleDataToggle.set(canUseSampleData(user));

        // Route guards only re-run on navigation, so switching accounts
        // while already mounted here would otherwise leave the previous
        // account's utilization data on screen.
        const accountName = user?.Account?.Name ?? null;
        if (this.previousAccountName === undefined) {
          this.previousAccountName = accountName;
          return;
        }
        if (this.previousAccountName !== accountName) {
          this.previousAccountName = accountName;
          this.getPage();
        }
      });
  }

  ngOnDestroy(): void {
    clearTimeout(this.debouncedGetPage);
    this.queryParamsSubscription?.unsubscribe();
  }

  onPageChange(pageNumber: number): void {
    this.pageIndex$.set(pageNumber);
  }

  toggleMockData(value: boolean): void {
    this.showMockData.set(value);
    this.getPage();
  }

  // undefined = not yet seen; tracks the account so a genuine switch (not
  // just any currentUser emission) triggers a reload.
  private previousAccountName: string | null | undefined = undefined;

  private debouncedGetPage: ReturnType<typeof setTimeout> | undefined;

  private queryParamsSubscription: import('rxjs').Subscription | undefined;

  private getPage(options?: { debounce?: number }): void {
    clearTimeout(this.debouncedGetPage);
    this.debouncedGetPage = setTimeout(
      () => {
        this.observations$.getPage({
          duration: this.selectedDuration$(),
          showMockData: this.showMockData(),
        });
      },
      this.debouncedGetPage === undefined ? 0 : (options?.debounce ?? 0),
    );
  }

  private applyQueryParams(params: Params): void {
    const durationParam = (params.duration ?? '') as string;
    if (
      (durationParam === '1w' ||
        durationParam === '1m' ||
        durationParam === '3m') &&
      durationParam !== this.selectedDuration$()
    ) {
      this.selectedDuration$.set(durationParam);
    }

    const searchParam = (params.search ?? '') as string;
    if (typeof searchParam === 'string' && searchParam !== this.searchTerm$()) {
      this.searchTerm$.set(searchParam);
    }

    const locationParam = (params.location ?? '') as string;
    if (
      typeof locationParam === 'string' &&
      locationParam !== this.selectedLocation$()
    ) {
      this.selectedLocation$.set(locationParam);
    }
  }

  public readonly urlSyncEffect = effect(() => {
    const duration = this.selectedDuration$();
    const search = (this.searchTerm$() || '').trim();
    const location = (this.selectedLocation$() || '').trim();

    this.router$.navigate([], {
      relativeTo: this.activatedRoute$,
      queryParamsHandling: 'merge',
      replaceUrl: true,
      queryParams: {
        duration,
        search: search || undefined,
        location: location || undefined,
      },
    });
  });

  canExport(): boolean {
    return (this.csvData$()?.length ?? 0) > 0;
  }

  public exportToCsv(): void {
    this.isExporting.set(true);
    try {
      const csvUnparse = this.papa$.unparse(
        this.csvData$() as unknown as Array<Record<string, string>>,
      );
      const csvBlob = new Blob([csvUnparse], {
        type: 'text/csv;charset=utf-8;',
      });
      const csvURL = window.URL.createObjectURL(csvBlob);
      const tempLink = document.createElement('a');
      tempLink.href = csvURL;
      tempLink.setAttribute('download', this.csvFileName);
      tempLink.click();
      window.URL.revokeObjectURL(csvURL);
    } finally {
      this.isExporting.set(false);
    }
  }

  private formatPercentage(value: number | null | undefined): string {
    if (value == null) return '';
    return `${value.toFixed(2)}%`;
  }

  private formatSpeed(value: number | null | undefined): string {
    if (value == null) return '';
    return `${value.toFixed(2)}`;
  }

  togglePageSizeDropdown(event: Event): void {
    event.stopPropagation();
    this.isPageSizeDropdownOpen.update((v) => !v);
  }

  closePageSizeDropdown(): void {
    this.isPageSizeDropdownOpen.set(false);
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize$.set(pageSize);
    this.pageIndex$.set(0);
    this.closePageSizeDropdown();
  }

  toggleLocationDropdown(event: Event): void {
    event.stopPropagation();
    this.isLocationDropdownOpen.update((v) => !v);
  }

  closeLocationDropdown(): void {
    this.isLocationDropdownOpen.set(false);
  }

  setLocation(location: string): void {
    this.selectedLocation$.set(location);
    this.pageIndex$.set(0);
    this.closeLocationDropdown();
  }

  clearLocation(): void {
    this.selectedLocation$.set('');
    this.pageIndex$.set(0);
    this.closeLocationDropdown();
  }

  toggleDurationDropdown(event: Event): void {
    event.stopPropagation();
    this.isDurationDropdownOpen.update((v) => !v);
  }

  closeDurationDropdown(): void {
    this.isDurationDropdownOpen.set(false);
  }

  selectDuration(duration: '1w' | '1m' | '3m'): void {
    this.selectedDuration$.set(duration);
    this.pageIndex$.set(0);
    this.closeDurationDropdown();
  }

  clearAllFilters(): void {
    this.searchTerm$.set('');
    this.selectedLocation$.set('');
    this.selectedDuration$.set('1m');
    this.pageIndex$.set(0);
  }
}
